import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import type { UsageByModel } from '@/types/service/internalAnalytics';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { CHART_COLORS, formatCurrency, formatNumber } from '@/utils/saasDashboardHelpers';

interface LynAIUsageByModelChartProps {
  data: UsageByModel[];
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    payload: {
      name: string;
      value: number;
      percentage: number;
      cost: number;
      inputCost: number;
      outputCost: number;
      color: string;
    };
  }>;
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <div className='bg-white p-3 border rounded shadow-lg'>
      <p className='font-medium' style={{ color: item.payload.color }}>
        {item.name}
      </p>
      <p className='text-sm text-muted-foreground'>
        {formatNumber(item.value)} tokens ({item.payload.percentage}%)
      </p>
      <p className='text-sm'>Total Cost: {formatCurrency(item.payload.cost)}</p>
      <p className='text-sm text-muted-foreground'>
        Input: {formatCurrency(item.payload.inputCost)} / Output:{' '}
        {formatCurrency(item.payload.outputCost)}
      </p>
    </div>
  );
};

const formatRate = (rate: string | null): string => {
  if (!rate) return 'N/A';
  const num = parseFloat(rate);
  if (num === 0) return '$0';
  // Format very small numbers in scientific notation or with many decimals
  if (num < 0.0001) {
    return `$${num.toExponential(2)}`;
  }
  return `$${num.toFixed(6)}`;
};

const LynAIUsageByModelChart = ({ data }: LynAIUsageByModelChartProps) => {
  if (data.length === 0) {
    return (
      <Card className='h-full'>
        <CardHeader>
          <CardTitle className='text-lg'>Usage by AI Model</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='h-[220px] flex items-center justify-center text-muted-foreground'>
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map((item, index) => ({
    name: item.ai_model,
    value: item.total_tokens,
    percentage: parseFloat(item.percentage_of_total),
    cost: parseFloat(item.total_cost_usd),
    inputCost: parseFloat(item.total_input_cost_usd),
    outputCost: parseFloat(item.total_output_cost_usd),
    inputRate: item.input_rate_per_token,
    outputRate: item.output_rate_per_token,
    color: CHART_COLORS.pie[index % CHART_COLORS.pie.length],
  }));

  return (
    <Card className='h-full'>
      <CardHeader>
        <CardTitle className='text-lg'>Usage by AI Model</CardTitle>
      </CardHeader>
      <CardContent>
        <div className='flex flex-col md:flex-row gap-4 items-start'>
          {/* Pie Chart */}
          <div className='flex-shrink-0'>
            <ResponsiveContainer width={180} height={180}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx='50%'
                  cy='50%'
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey='value'
                  nameKey='name'
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Legend with Cost Rates */}
          <div className='flex-1 space-y-3'>
            {chartData.map((item, index) => (
              <div key={index} className='border-b pb-2 last:border-b-0'>
                <div className='flex items-center gap-2 text-sm'>
                  <div
                    className='w-3 h-3 rounded-sm flex-shrink-0'
                    style={{ backgroundColor: item.color }}
                  />
                  <span className='flex-1 font-medium truncate'>{item.name}</span>
                  <span className='text-muted-foreground'>
                    {formatNumber(item.value)} ({item.percentage}%)
                  </span>
                </div>
                <div className='ml-5 mt-1 flex gap-2 flex-wrap'>
                  <Badge variant='outline' className='text-xs'>
                    Input: {formatRate(item.inputRate)}/token
                  </Badge>
                  <Badge variant='outline' className='text-xs'>
                    Output: {formatRate(item.outputRate)}/token
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LynAIUsageByModelChart;
